import { CustomTextFieldShadcn } from "@/dentlab/src/forms/form-input-components/custom-input-components/custom-text-field-shadcn.component"
import { OnboardingStep } from "../onboarding-components/onboarding-step"
import { useState, useEffect } from "react";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useUserManagementStore } from "@/dentlab/src/store/UserManagement";
import { useAttributesStore } from "@/dentlab/src/store/Attributes/attributes.store";
import { ONBOARDING_STEPS, ONBOARDING_STATUS } from "@/dentlab/src/constants/onboarding";
import { ChatMockup } from "../onboarding-components/onboarding-mockups/ChatMockup";
import { ImageInput } from "@/dentlab/src/forms/form-components/client/input-components/image-input";
import { StorageBucketsEnum } from "@/dentlab/src/types/enums";
import { StepProps } from "./types";

interface PersonalInfoValidationParams {
    firstName: string;
    lastName: string;
}

const validatePersonalInfo = (params: PersonalInfoValidationParams): string | null => {
    if (!params.firstName.trim()) return "Bitte geben Sie Ihren Vornamen ein";
    if (!params.lastName.trim()) return "Bitte geben Sie Ihren Nachnamen ein";
    return null;
};

interface StepPersonalInformationProps extends StepProps {}

export const StepPersonalInformation: React.FC<StepPersonalInformationProps> = ({ onComplete, stepNumber, totalSteps, canGoBack, onGoBack }) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

    const { profile, organization } = useCentralStore((state) => ({
        profile: state.profile,
        organization: state.organization,
    }));
    const { updateProfile, uploadProfileAvatar } = useUserManagementStore((state) => ({
        updateProfile: state.updateProfile,
        uploadProfileAvatar: state.uploadProfileAvatar,
    }));
    const setOnboardingAttribute = useAttributesStore((state) => state.setOnboardingAttribute);

    useEffect(() => {
        if (profile?.first_name && !firstName) {
            setFirstName(profile.first_name);
        }
        if (profile?.last_name && !lastName) {
            setLastName(profile.last_name);
        }
        // We dont need to set the avatar_url; it's set in the ImageInput component
    }, []);

    const handleFileChange = async (file: File | null) => {
        if (!file) return;
        await uploadProfileAvatar(file, { showNotification: false });
    };

    const handleSubmit = async () => {
        const error = validatePersonalInfo({ firstName, lastName });
        if (error) {
            setErrorMessage(error);
            return;
        }

        if (!profile?.id) {
            setErrorMessage("Benutzerprofil nicht gefunden");
            return;
        }

        setLoading(true);
        setErrorMessage(null);

        const success = await updateProfile(profile.id, {
            first_name: firstName.trim(),
            last_name: lastName.trim(),
        });

        if (!success) {
            setErrorMessage("Fehler beim Speichern der Daten");
            setLoading(false);
            return;
        }

        await setOnboardingAttribute(ONBOARDING_STEPS.PERSONAL_INFO, ONBOARDING_STATUS.COMPLETED, 'org');
        setLoading(false);
        onComplete();
    }
    const initials = `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();

    return (
        <OnboardingStep
            stepTitle="Ihr Nutzeraccount"
            stepDescription="Ihr Vorname und Nachname werden im Chat angezeigt."
            stepNumber={stepNumber}
            totalSteps={totalSteps}
            onButtonClick={handleSubmit}
            canGoBack={canGoBack}
            onGoBackClick={onGoBack}
            buttonText="Weiter"
            buttonDisabled={false}
            loading={loading}
            errorMessage={errorMessage}
            onErrorMessageClose={() => setErrorMessage(null)}
            rightPanel={<ChatMockup firstName={firstName} lastName={lastName} avatarUrl={avatarPreview} />}
        >
            <div className="space-y-4 mx-auto">
                <div className="flex items-center gap-4">
                    <ImageInput
                        bucket={StorageBucketsEnum.PUBLIC_IMAGES}
                        organizationId={organization?.id ?? ""}
                        avatarPath={profile?.avatar_url ?? ""}
                        onFileChange={handleFileChange}
                        initials={initials}
                        onPreviewChange={(url) => setAvatarPreview(url)}
                    />
                    <div className="flex-1">
                        <div className="text-sm font-medium text-gray-700">Profilbild (optional)</div>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                    <CustomTextFieldShadcn
                        label="Vorname"
                        id="firstName"
                        name="firstName"
                        autoComplete="given-name"
                        required
                        autoFocus
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        classNameInput="h-9 sm:h-10"
                        data-testid="signup-firstname-input"
                    />
                    <CustomTextFieldShadcn
                        label="Nachname"
                        id="lastName"
                        name="lastName"
                        autoComplete="family-name"
                        required
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        classNameInput="h-9 sm:h-10"
                        data-testid="signup-lastname-input"
                    />
                </div>
            </div>
        </OnboardingStep>
    )
}